import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {ScrollSection} from 'components/ScrollSpy'
import {useAccommodationPageScrollTopOffset} from 'hooks/useAccommodationPageScrollTopOffset'
import {getScrollTargetSpecificRoom} from 'hooks/useHandleScrollToClickedOfferTarget/utils'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getRoomContext} from 'middleware/analytics/selectors'
import {OverlayType} from 'modules/overlay/types'
import {getOptimizedRoomsConfig} from 'modules/sapiSearch/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {roomsStringToRoomsSplit} from '@vio/core/src/room/business/roomConfiguration'

import {RoomCardDesktop} from './Desktop/RoomCardDesktop'
import {RoomCardMobile} from './Mobile/RoomCardMobile'
import {RoomCardHeaderLayout} from './RoomCardHeaderLayout'
import {RoomCardProps} from './types'

export const RoomCard = (props: RoomCardProps) => {
  const {room, hotelId} = props
  const roomsString = useSelector(state =>
    getOptimizedRoomsConfig(state, hotelId)
  )
  const dispatch = useDispatch()
  const roomContext = useSelector(state => getRoomContext(state, room.id))
  const roomsSplit = roomsStringToRoomsSplit(roomsString)

  const {isMobile, isDesktopXs} = useDeviceLayout()
  const scrollTopOffset = useAccommodationPageScrollTopOffset()
  const showOverlay = useShowOverlay()
  const RoomCardLayout =
    isMobile || isDesktopXs ? RoomCardMobile : RoomCardDesktop

  const onOpenRoomDetailsOverlay = () => {
    showOverlay({
      type: OverlayType.RoomDetailsOverlay,
      params: {roomId: room.id, hotelId}
    })
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.RoomDetailsButton,
        action: Action.Clicked,
        team: Team.Select,
        page: Page.Accommodation,
        analyticsContext: {
          [AnalyticsContext.RoomContext]: roomContext
        }
      })
    )
  }
  const isDetailsLinkVisible =
    room.description?.length > 0 ||
    room.amenities?.length > 0 ||
    room.images?.length > 0
  return (
    <ScrollSection
      // Same offset the section anchors use: fixed header plus the sticky
      // search box, or card scrolls tuck the room name under the sticky bar
      scrollTopMargin={scrollTopOffset}
      name={getScrollTargetSpecificRoom(room.id)}
      as="div"
      dataId={getScrollTargetSpecificRoom(room.id)}
      // room.id rotates per search; the masterId is what scrollToRoom deep
      // links carry, so expose it for QA and test verification
      dataAttributes={{'data-room-master-id': room.masterId}}
    >
      <RoomCardHeaderLayout
        dataId="defaultRoomCard"
        {...(props.isFrozen && {
          bgColorPath: 'background.brand.secondary',
          contentColorPath: 'content.brand.primary',
          borderColorPath: 'border.brand.primary',
          isHighlighted: true,
          iconSlot: <Icon name="ArrowDown" />,
          titleSlot: (
            <Text variant="labelM">
              <FormattedMessageWrapper
                id="dealFreeze.yourFrozenDeal"
                defaultMessage="Your frozen deal"
              />
            </Text>
          )
        })}
      >
        <RoomCardLayout
          {...props}
          roomsSplit={roomsSplit}
          isDetailsLinkVisible={isDetailsLinkVisible}
          onOpenRoomDetailsOverlay={onOpenRoomDetailsOverlay}
        />
      </RoomCardHeaderLayout>
    </ScrollSection>
  )
}
